import superagent from 'superagent';

import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {DecisionKind} from '@viamedici-spc/configurator-ts';

import {capitalize} from '../util/capitilize';
import toJS from '../util/toJS';
import {spcToStoredDecisions} from '../components/spc/util/spcUtils';
import {
	selectSpcAttributes,
	selectSpcConfiguration,
	selectSpcIsSatisfied,
	selectSpcSession,
	selectVariantCodeTypes
} from '../selectors';
import {getValueOfFeature} from '../util/values';
import {conditional} from '../util/conditional';
/*
import {
	selectSolarActivityLevel,
	selectSolarAzimuth,
	selectSolarAzimuthAngle,
	selectSolarDimmProfile,
	selectSolarLocation,
	selectSolarShadowLevel
} from '../solar/solarSelectors';
*/

export const fetchConfiguration = createAsyncThunk(
	'configuration/configurationLoading',
	(key, {getState}) => superagent
		.get(`${getState().status.apiUrl}/configuration/${key ?? getState().status.dataKey}`)
		.set('Accept', 'application/json')
		.then((response) => response.body)
		.catch((error) => error)
);

export const storeConfiguration = createAsyncThunk(
	'configuration/store',
	async (options, {getState}) => {
		const state = getState();
		//console.log('storeConfiguration', buildConfiguration(state));
		return superagent
			.put(`${getState().status.apiUrl}/configuration/${getState().status.dataKey}`)
			.send({configuration: buildConfiguration(state)})
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error);
	}
);

export const finalizeConfiguration = createAsyncThunk(
	'configuration/finalizeConfiguration',
	(key, {getState}) => {
		const state = getState();
		return superagent
			.post(`${state.status.apiUrl}/configuration/${key ?? state.status.dataKey}/finalize`)
			.send({
					configuration: buildConfiguration(state, true),
					csv: buildCSV(state),
					family: state.status.family,
					region: state.status.region,
					language: state.status.lang,
					mode: state.status.mode,
					in: state.userProfile.data.loggedIn ? '2' : ''
				},
			)
			.set('Accept', 'application/json')
			.then((response) => response.body)
			.catch((error) => error);
	}
);

const configurationInitialState = {
	status: 'idle',
	data: {
		formatVersion: 24,
		dataKey: '',
		changesCount: 0,
		created: new Date().toISOString().split('T')[0],
		updated: new Date().toISOString().split('T')[0],
		finalized: false,
		projects: [
			{
				key: '',
				name: '',
				configuration: null,
				sort: 1,
				created: null,
				updated: null,
				project: {
					source: 'external',
					lastPersistedBy: 'External',
					anonymousUserId: '',
					featureModelId: '',
					productModelId: '',
					ready: false,
					valid: false,
					lang: 'de',
					name: '',
					region: 'int',
					decisions: [],
					lightProfileData: null,
					solarData: null,
					olivioData: null,
					dimmLevelData: null,
					personalisation: null,
					images: {
						default: null,
						top: null,
						side: null,
						lightProfileYear: null
					},
					dailySegments: [],
					tenderText: '',
					relux: null,
				},
			}
		]
	},
	error: {},
};

// slice state selectors
const selectProject = state => state.data.projects[0].project;
export const selectConfigPersonalisation = state => selectProject(state)?.personalisation;
export const selectConfigHasPersonalisation = state => {
	const perso = selectConfigPersonalisation(state);
	if (!perso) return false;
	for (const key in perso) {
		if (perso[key] === true || perso[key].length > 0) return true;
	}
	return false;
};


export const configurationSlice = createSlice({
	name: 'configuration',
	initialState: configurationInitialState,
	reducers: {
		setFeatureModel: (state, action) => {
			selectProject(state).featureModelId = action.payload.featureModel;
		},
		setProductModel: (state, action) => {
			selectProject(state).productModelId = action.payload.productModel;
		},
		setDimmLevel: (state, action) => {
			selectProject(state).dimmLevelData = action.payload;
		},
		updatePersonalisation: (state, action) => {
			const {name, value} = action.payload;
			const project = selectProject(state);
			if (!project) return;
			if (!project.personalisation) {
				project.personalisation = {};
			}
			project.personalisation[name] = value;
			if (!selectConfigHasPersonalisation(state)) {
				project.personalisation = null;
			}
		},
		finalize: (state, action) => {
			state.data.finalized = true;
		},
		unFinalize: (state, action) => {
			state.data.finalized = false;
		},
		downloadCSV: (state, action) => {
			const project = selectProject(state);
			const projectName = project.info?.projectName || 'decisions';
			const exportDecisions = Object.assign({}, project.decisions);
			exportDecisions.project = project.decisions;
			downloadAsCSV(action.payload.csvContent, projectName);
		},
		saveCanvasImage: (state, action) => {
			const project = selectProject(state);
			if (!project) return;
			if (!project.images) {
				project.images = {
					default: null,
					top: null,
					side: null,
					lightProfileYear: null
				};
			}
			console.log('saveCanvasImage', action.payload);
			state.data.projects[0].project.images = {
				...project.images,
				[action.payload.viewType || 'default']: action.payload.dataUrl
			};
		},
		saveTenderText: (state, action) => {
			const project = selectProject(state);
			if (!project) return;
			if (!project.tenderText) {
				project.tenderText = '';
			}
			project.tenderText = action.payload.tenderText;
			//console.log('saveTenderText', project, project.tenderText);
		},
		saveRelux: (state, action) => {
			const project = selectProject(state);
			if (!project) return;
			if (!project.relux) {project.relux = null;}
			project.relux = action.payload.relux;
			console.log('saveRelux', project, project.relux);
		},
		saveLightProfileDailySegments: (state, action) => {
			state.data.projects[0].project.dailySegments = action.payload.dailySegments;
			console.log('saveLightProfileDailySegments', action.payload.dailySegments);
		}
	},
	extraReducers: (builder) => {
		builder.addCase(fetchConfiguration.fulfilled, (state, action) => {
			if (action.payload.status === 404) {
				state.data = configurationInitialState.data;
			} else {
				state.data = action.payload.data;
			}
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(fetchConfiguration.pending, (state, action) => {
			//state.data = {};
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(fetchConfiguration.rejected, (state, action) => {
			//state.data = {};
			state.status = 'idle';
			state.error = action.payload;
		});
		builder.addCase(storeConfiguration.fulfilled, (state, action) => {
			//console.log('storeConfiguration.fulfilled', action);
			//state.data.dataKey = action.payload.data.dataKey;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(storeConfiguration.pending, (state, action) => {
			//state.data = [];
			state.status = 'storing';
			state.error = {};
		});
		builder.addCase(storeConfiguration.rejected, (state, action) => {
			//state.data = [];
			state.status = 'idle';
			state.error = action.payload;
		});

		builder.addCase(finalizeConfiguration.fulfilled, (state, action) => {
			console.log('finalizeConfiguration.fulfilled', action);
			state.data.finalized = true;
			state.status = 'idle';
			state.error = {};
		});
		builder.addCase(finalizeConfiguration.pending, (state, action) => {
			//state.data = {};
			console.log('finalizeConfiguration.pending', action);
			state.status = 'loading';
			state.error = {};
		});
		builder.addCase(finalizeConfiguration.rejected, (state, action) => {
			console.log('finalizeConfiguration.rejected', action);

			//state.data = {};
			state.status = 'idle';
			state.error = action.payload;
		});
	}
});

//sends csvContent Array as csv file to browser
function downloadAsCSV(csvContent, name) {
	const separator = ';';

	const csv = 'data:text/csv;charset=utf-8,' +
		csvContent.map(row => row.join(separator)).join('\n');

	const downloadAnchorNode = document.createElement('a');
	downloadAnchorNode.setAttribute('href', encodeURI(csv));
	downloadAnchorNode.setAttribute('download', name + '.csv');
	document.body.appendChild(downloadAnchorNode);
	downloadAnchorNode.click();
	downloadAnchorNode.remove();
}

function buildCSV(state) {
	const conditionalVariantValues = state.productData.conditionalVariantValues;
	const price = state.price.data;
	const spcConfiguration = selectSpcConfiguration(state);
	const spcAttributes = selectSpcAttributes(state);
	const shortNumberProductModel = state.shortNumber.data.ProductModel ?? '';

	const userProfile = state.userProfile.data;
	const info = state.configuration.data.projects[0].project.info;
	const status = state.status;
	const lightProfile = state.lightProfile.data;
	const isSolar = state.status.family === 'Aeto';

	const csvContent = [];
	csvContent.push(['ID', 'VALUE']);
	if (spcAttributes) {
		csvContent.push(...spcAttributes.map(spcAttribute => {
					const valueId = getValueOfFeature(spcAttribute.key, spcConfiguration);
					// const featureLabel = pimText(this.lang, featureId, 'F', this.decisions, this.state.productData.translations);
					// const valueLabel = pimText(this.lang, featureId, 'FV', this.decisions, this.state.productData.translations, valueId);
					return [spcAttribute.key, valueId];
				}
			)
		);
	}
	csvContent.push(['', '']);


	const ccvKeys = [];
	// get the ids
	Object.keys(conditionalVariantValues).forEach(ccv => {
		const ccvKeyParts = ccv.split('.');
		const ccvKeyPart = ccvKeyParts[ccvKeyParts.length - 2];
		ccvKeys.push(ccvKeyPart);
	});
	const conditionals = [];
	ccvKeys.forEach(key => {
		const cond = conditional(spcConfiguration, conditionalVariantValues, key);
		if (cond !== null && conditionals.findIndex(c => c.id === key) === -1) {
			conditionals.push({
				id: key,
				value: cond,
			});
		}
	});
	csvContent.push(...conditionals.map(c => [c.id, c.value]));

	csvContent.push(['', '']);

	if (isSolar) {
		/*
		csvContent.push(['location', selectSolarLocation(state)?.name]);
		csvContent.push(['activity', selectSolarActivityLevel(state)]);
		csvContent.push(['shadowLevel', selectSolarShadowLevel(state)]);
		csvContent.push(['azimuth', selectSolarAzimuth(state)]);
		csvContent.push(['azimuth angle', selectSolarAzimuthAngle(state)]);
		csvContent.push(['dimmProfile', selectSolarDimmProfile(state)]);
		csvContent.push(['', '']);
		 */
	}

	csvContent.push(['totalPriceInEuro', price.totalPriceInEuro]);
	csvContent.push(['totalBuildCosts', price.totalBuildCosts]);
	csvContent.push(['totalPriceIsNotFinal', price.totalPriceIsNotFinal]);

	csvContent.push(['', '']);

	if (price.conditionalPrices && price.conditionalPrices.length > 0) {
		price.conditionalPrices.forEach(cp => {
			csvContent.push([cp.description + ' price (' + cp.priceType + ')', cp.priceInEuro]);
			csvContent.push([cp.description + ' build price (' + cp.priceType + ')', cp.buildPriceInEuro]);
		});

		csvContent.push(['', '']);
	}

	csvContent.push(...selectVariantCodeTypes(state).map(
		variantCode => [variantCode.id, variantCode.code]
	));

	csvContent.push(['', '']);

	csvContent.push(['shortNumber', shortNumberProductModel]);

	//console.log('buildCSV', csvContent);
	const separator = ';';

	return csvContent.map(row => row.join(separator)).join('\n');
}

function buildConfiguration(state, final = false) {

	// create decisions from current decision state when available, otherwise use loaded configuration
	const spcSession = selectSpcSession(state);
	const spcConfiguration = selectSpcConfiguration(state);
	let spcDecisions = [];
	if (spcSession) {
		spcDecisions = spcToStoredDecisions(spcSession.getDecisions(DecisionKind.Explicit));
	}
	const configurationDecisions = state.configuration?.data?.projects[0]?.project?.spcDecisions;
	const decisions = spcDecisions?.length > 0 ? spcDecisions : configurationDecisions ?? [];

	const featureModelId = state.configuration?.data?.projects[0]?.project?.featureModelId;
	const productModelId = state.configuration?.data?.projects[0]?.project?.productModelId;
	const lightProfileData = {
		project: state.lightProfile?.data.project,
	};
	const solarData = {
		project: state.solar?.data.project,
	};
	const olivioData = state.olivio?.data;
	const dimmLevelData = state.configuration?.data?.projects[0]?.project?.dimmLevelData;
	const personalisation = state.configuration?.data?.projects[0]?.project?.personalisation;
	const images = state.configuration?.data?.projects[0]?.project?.images;
	const dailySegments = state.configuration?.data?.projects[0]?.project?.dailySegments;
	const tenderText = state.configuration?.data?.projects[0]?.project?.tenderText;
	const relux = state.configuration?.data?.projects[0]?.project?.relux;
	const configuration = {
		formatVersion: state.status.formatVersion ?? 24,
		dataKey: state.status.dataKey,
		rootKey: state.status.dataKey,
		changesCount: state.configuration?.data.changesCount + 1,
		finalized: final || state.configuration.data.finalized,
		projects: [
			{
				key: state.status.dataKey,
				name: state.configuration?.data.name ?? '',
				sort: state.configuration?.data.sort ?? 1,
				project: {
					lang: state.status.lang,
					region: state.status.region,
					key: state.status.dataKey,
					name: state.configuration?.data.name ?? '',
					lastPersistedBy: capitalize(state.status.mode),
					source: capitalize(state.status.mode),
					featureModelId: featureModelId,
					productModelId: productModelId,
					ready: true,
					valid: selectSpcIsSatisfied(state),
					sort: 1,
					decisions: decisions,
					lightProfileData: lightProfileData,
					solarData: solarData,
					olivioData: olivioData,
					dimmLevelData: dimmLevelData,
					personalisation: personalisation,
					images: images,
					tenderText: tenderText,
					dailySegments: dailySegments,
					relux: relux,
					info: {}
				}
			}
		]
	};
	//console.log('buildConfiguration', configuration);
	return configuration;
}

export const {
	setFeatureModel,
	setProductModel,
	setDimmLevel,
	updatePersonalisation,
	saveCanvasImage,
	saveTenderText,
	saveRelux,
	saveLightProfileDailySegments,
	downloadCSV,
	finalize,
	unFinalize,
} = configurationSlice.actions;


export default configurationSlice.reducer;
